import { StatusCodes } from 'http-status-codes'

// Returned by create flow: this user already exists in the account
const USER_ALREADY_EXISTS_CODE = 'user_already_exists'
// Returned by update flow: the new email is already in use in Keycloak
const EMAIL_ALREADY_IN_USE_CODE = 'email_already_in_use'
// Returned by the create and update flows: the account's Workflow Team Member seat limit is reached.
// Exported because the bulk import path returns the same code in a different envelope — a 422
// carrying it per problem at `extra.errors[i].code` rather than a 409 at `extra.code` — so the
// classifier below cannot be shared, but the identifier must not be written down twice.
export const SEAT_LIMIT_EXCEEDED_CODE = 'seat_limit_exceeded'
// Returned by the create and update flows: the signed-in user lacks the Workflow permission that
// governs the requested user type (Manage Team Members / Manage Admin Users)
const INSUFFICIENT_MANAGE_PERMISSION_CODE = 'insufficient_manage_permission'
// Returned by the update flow: the account user is federated, so its identity fields belong to the
// customer's IdP and GIM refuses to write them
const IDENTITY_MANAGED_BY_SSO_CODE = 'identity_managed_by_sso'
// Returned by the create and update flows: the backend could not reach a trustworthy answer from
// Workflow about the above, and refuses rather than letting the check lapse silently
const PERMISSION_CHECK_UNAVAILABLE_CODE = 'permission_check_unavailable'

/**
 * Whether an RTK Query rejection is the given backend status carrying the given
 * `extra.code`. The backend's error envelope is `{ message, extra: { code } }`
 * (core.exception_handler), so every domain classifier below is this same shape
 * with different constants.
 */
function hasApplicationErrorCode(
  error: unknown,
  status: StatusCodes,
  code: string,
): boolean {
  if (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'data' in error
  ) {
    // RTK Query's unwrap() rejects with a plain object; structural checks above narrow it
    const e = error as { status: unknown; data: unknown }
    if (e.status === status && typeof e.data === 'object' && e.data !== null) {
      const data = e.data as { extra?: { code?: string } }
      return data.extra?.code === code
    }
  }
  return false
}

export function isEmailConflictError(error: unknown): boolean {
  return (
    hasApplicationErrorCode(
      error,
      StatusCodes.BAD_REQUEST,
      USER_ALREADY_EXISTS_CODE,
    ) ||
    hasApplicationErrorCode(
      error,
      StatusCodes.BAD_REQUEST,
      EMAIL_ALREADY_IN_USE_CODE,
    )
  )
}

/**
 * Any 403, coded or not. Broader than `isInsufficientManagePermissionError`, so
 * check that one first wherever both are used.
 */
export function isForbiddenError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    // RTK Query's unwrap() rejects with a plain object; structural check above narrows it
    const e = error as { status: unknown }
    return e.status === StatusCodes.FORBIDDEN
  }
  return false
}

// What GIM's exception handler puts in `message` for a DRF field-validation
// failure. It names no field and reads as jargon, so it is not worth showing.
const GENERIC_VALIDATION_MESSAGE = 'Validation error'

/**
 * The backend's client-facing error message for a 4xx response, when it carries
 * a useful one. GIM wraps deliberate domain failures as `{ message, extra }`
 * (see `core.exception_handler`) and that message is written to be read by the
 * end user, so it explains the reason far better than a generic "could not
 * create user" string (KPS-513). 5xx responses are deliberately excluded — their
 * messages describe internals and should stay behind the generic message.
 */
export function getBackendErrorMessage(error: unknown): string | undefined {
  if (
    typeof error !== 'object' ||
    error === null ||
    !('status' in error) ||
    !('data' in error)
  ) {
    return undefined
  }
  // RTK Query's unwrap() rejects with a plain object; structural checks above narrow it
  const e = error as { status: unknown; data: unknown }
  if (
    typeof e.status !== 'number' ||
    e.status < StatusCodes.BAD_REQUEST ||
    e.status >= StatusCodes.INTERNAL_SERVER_ERROR
  ) {
    return undefined
  }
  if (typeof e.data !== 'object' || e.data === null) {
    return undefined
  }
  const { message } = e.data as { message?: unknown }
  if (typeof message !== 'string') {
    return undefined
  }
  const trimmed = message.trim()
  return trimmed && trimmed !== GENERIC_VALIDATION_MESSAGE ? trimmed : undefined
}

export function isInsufficientManagePermissionError(error: unknown): boolean {
  return hasApplicationErrorCode(
    error,
    StatusCodes.FORBIDDEN,
    INSUFFICIENT_MANAGE_PERMISSION_CODE,
  )
}

/**
 * Whether an update was refused because the user's identity is federated. The form
 * already disables those fields for a federated user, so this is the backstop for a
 * user who became federated while the page was open.
 */
export function isIdentityManagedBySsoError(error: unknown): boolean {
  return hasApplicationErrorCode(
    error,
    StatusCodes.BAD_REQUEST,
    IDENTITY_MANAGED_BY_SSO_CODE,
  )
}

export function isPermissionCheckUnavailableError(error: unknown): boolean {
  return hasApplicationErrorCode(
    error,
    StatusCodes.SERVICE_UNAVAILABLE,
    PERMISSION_CHECK_UNAVAILABLE_CODE,
  )
}

export function isSeatLimitError(error: unknown): boolean {
  return hasApplicationErrorCode(
    error,
    StatusCodes.CONFLICT,
    SEAT_LIMIT_EXCEEDED_CODE,
  )
}
